import { useLocation } from 'react-router';
import { Sheet, SheetContent, SheetTitle } from '@giyus/ui';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StepUpDialog } from '../components/StepUpDialog';
import { useMe } from '../lib/auth/authContext';
import { useStepUp } from '../lib/auth/useStepUp';
import { browserPreferenceStorage, readContextPreference } from '../lib/context/contextPreferences';
import { resolveEffectiveScope } from '../lib/context/contextResolver';
import { useContextStore } from '../lib/context/contextStore';
import { APP_VERSION } from '../lib/env';
import { PortalExperienceProvider } from '../portal/experience/PortalExperienceProvider';
import { BreakGlassBanner } from './BreakGlassBanner';
import { ErrorBoundary } from './ErrorBoundary';
import { Sidebar } from './Sidebar';
import { Toaster } from './Toaster';
import { Topbar } from './Topbar';

export interface AppShellProps {
  readonly children: ReactNode;
}

function StepUpHost() {
  const { open, confirm, cancel } = useStepUp();
  return <StepUpDialog open={open} onConfirm={confirm} onCancel={cancel} />;
}

export function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation(['common', 'shell']);
  const me = useMe();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isNetworkScope = useContextStore((s) => s.isNetworkScope);
  const activeTenantCode = useContextStore((s) => s.activeTenantCode);
  const mirror = useContextStore((s) => s.mirror);
  const effectiveScope = useMemo(() => {
    const remembered = readContextPreference(browserPreferenceStorage(), me.user.id);
    return resolveEffectiveScope(me, isNetworkScope, activeTenantCode, remembered);
  }, [me, isNetworkScope, activeTenantCode]);
  const hasScope = effectiveScope.hasScope && location.pathname !== '/no-access';

  useEffect(() => {
    const isScopedRoute =
      location.pathname === '/network' ||
      location.pathname.startsWith('/network/') ||
      location.pathname.startsWith('/t/');
    const current = useContextStore.getState();
    if (
      !isScopedRoute &&
      !current.isNetworkScope &&
      current.activeTenantCode === null &&
      effectiveScope.hasScope &&
      location.pathname !== '/select-context' &&
      location.pathname !== '/no-access'
    ) {
      mirror({
        userId: me.user.id,
        isNetworkScope: effectiveScope.isNetworkScope,
        activeTenantCode: effectiveScope.activeTenantCode,
        activeTenantId: effectiveScope.activeTenantId,
        activeSiteId: effectiveScope.activeSiteId,
      });
    }
  }, [effectiveScope, location.pathname, me.user.id, mirror]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 768px)');
    const closeOnDesktop = () => {
      if (desktop.matches) setSidebarOpen(false);
    };
    desktop.addEventListener('change', closeOnDesktop);
    return () => {
      desktop.removeEventListener('change', closeOnDesktop);
    };
  }, []);

  if (location.pathname === '/select-context') {
    return (
      <main id="main">
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>
    );
  }

  return (
    <PortalExperienceProvider userEmail={me.user.email}>
      <div
        className="giyus-app-bg flex min-h-svh flex-col bg-background text-foreground md:flex-row"
        data-testid="app-shell"
      >
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground"
        >
          {t('common:a11y.skipToContent')}
        </a>

        {hasScope && (
          <>
            <Sidebar open={false} />
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetContent
                side="left"
                closeLabel={t('common:actions.close')}
                aria-describedby={undefined}
                className="w-[min(20rem,calc(100vw-2rem))] gap-0 p-0"
                onCloseAutoFocus={(event) => {
                  event.preventDefault();
                  document.getElementById('sidebar-toggle')?.focus();
                }}
              >
                <SheetTitle className="sr-only">{t('common:a11y.mainNavigation')}</SheetTitle>
                <Sidebar
                  open
                  drawer
                  onNavigate={() => {
                    setSidebarOpen(false);
                  }}
                />
              </SheetContent>
            </Sheet>
          </>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar
            sidebarOpen={sidebarOpen}
            showContextSelector={hasScope}
            {...(hasScope
              ? {
                  onToggleSidebar: () => {
                    setSidebarOpen((open) => !open);
                  },
                }
              : {})}
          />
          <BreakGlassBanner />
          <main
            id="main"
            tabIndex={-1}
            className="mx-auto w-full max-w-(--shell-content-max) flex-1 px-(--page-gutter) py-6"
          >
            <ErrorBoundary>{children}</ErrorBoundary>
          </main>
          <footer className="border-t border-border">
            <div className="px-6 py-3 text-caption text-muted-foreground">
              {t('shell:footer.version', { version: APP_VERSION })}
            </div>
          </footer>
        </div>

        <StepUpHost />
        <Toaster />
      </div>
    </PortalExperienceProvider>
  );
}
